import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import Navbar from "../components/Navbar";
import { useAuth } from "../context/AuthContext";
import { Star, Send, Loader2, ArrowLeft, MessageSquare } from "lucide-react";
import { toast } from "sonner";

const ROLE_LABELS = {
    alternant: "Alternant",
    formateur: "Formateur",
    admin: "Administrateur",
};

export default function ReviewsPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [rating, setRating] = useState(0);
    const [hoverRating, setHoverRating] = useState(0);
    const [comment, setComment] = useState("");
    const [name, setName] = useState("");
    const [etablissement, setEtablissement] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const initial = `${user?.prenom || ""} ${user?.nom || ""}`.trim() || (user?.email?.split("@")[0] || "");
        setName(initial);
        setEtablissement(user?.nom_etablissement || "");
        (async () => {
            try {
                const { data } = await api.get("/reviews/published");
                setReviews(data);
            } catch (e) {
                console.warn("Reviews: failed to load", e);
            } finally { setLoading(false); }
        })();
    }, [user]);

    const submit = async (e) => {
        e.preventDefault();
        if (rating < 1) {
            toast.error("Choisissez une note (1 à 5 étoiles).");
            return;
        }
        if (comment.trim().length < 10) {
            toast.error("Votre avis doit faire au moins 10 caractères.");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/reviews", {
                rating,
                name: name || "Utilisateur Profawz",
                role_label: ROLE_LABELS[user?.role] || "Utilisateur",
                etablissement: etablissement || null,
                comment: comment.trim(),
            });
            toast.success("Merci pour votre avis !");
            const { data } = await api.get("/reviews/published");
            setReviews(data);
            setComment("");
            setRating(0);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Échec de l'envoi");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-paper">
            <Navbar />
            <main className="max-w-4xl mx-auto px-6 lg:px-10 py-10" data-testid="reviews-page">
                <button onClick={() => navigate(-1)} className="text-sm font-mono text-muted2 inline-flex items-center gap-1 hover:text-ink mb-6">
                    <ArrowLeft size={14} /> Retour
                </button>

                <div className="label-eyebrow mb-2">Vos retours</div>
                <h1 className="h-display text-4xl sm:text-5xl font-semibold leading-tight">
                    Donnez votre avis sur <span className="text-orange">Profawz</span>
                </h1>
                <p className="text-sm text-muted2 mt-3 max-w-2xl">
                    Vos retours aident à améliorer la plateforme et à convaincre d'autres établissements de l'adopter. Les avis publiés apparaîtront sur la page d'accueil publique.
                </p>

                {/* FORM */}
                <form onSubmit={submit} className="panel p-6 sm:p-8 mt-10" data-testid="review-form">
                    <div className="label-eyebrow mb-3">Votre note</div>
                    <div className="flex gap-2 mb-6" role="radiogroup" data-testid="rating-stars">
                        {[1, 2, 3, 4, 5].map((n) => (
                            <button
                                type="button"
                                key={n}
                                onMouseEnter={() => setHoverRating(n)}
                                onMouseLeave={() => setHoverRating(0)}
                                onClick={() => setRating(n)}
                                className="p-2 transition-transform hover:scale-110"
                                aria-label={`${n} étoiles`}
                                data-testid={`rating-star-${n}`}
                            >
                                <Star
                                    size={32}
                                    className={n <= (hoverRating || rating) ? "text-orange" : "text-rule"}
                                    fill={n <= (hoverRating || rating) ? "currentColor" : "none"}
                                    strokeWidth={1.5}
                                />
                            </button>
                        ))}
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4 mb-4">
                        <div>
                            <label className="label-eyebrow mb-2 block">Votre prénom (visible publiquement)</label>
                            <input
                                className="field-input"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Lucas"
                                data-testid="review-name-input"
                            />
                        </div>
                        <div>
                            <label className="label-eyebrow mb-2 block">Établissement (optionnel)</label>
                            <input
                                className="field-input"
                                value={etablissement}
                                onChange={(e) => setEtablissement(e.target.value)}
                                placeholder="Lycée Sainte Famille"
                                data-testid="review-etablissement-input"
                            />
                        </div>
                    </div>

                    <label className="label-eyebrow mb-2 block">Votre avis</label>
                    <textarea
                        rows={5}
                        className="field-input resize-none"
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        placeholder="Qu'est-ce que vous appréciez sur Profawz ? Qu'est-ce qui pourrait être amélioré ?"
                        maxLength={600}
                        data-testid="review-comment-input"
                    />
                    <div className="text-xs text-muted2 mt-1 text-right font-mono">{comment.length}/600</div>

                    <div className="mt-6 flex justify-end">
                        <button type="submit" disabled={submitting} className="btn-primary" data-testid="review-submit-button">
                            {submitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                            {submitting ? "Envoi…" : "Publier mon avis"}
                        </button>
                    </div>
                </form>

                {/* PUBLISHED REVIEWS */}
                <section className="mt-16">
                    <div className="flex items-baseline gap-3 pb-3 border-b-2 border-ink">
                        <MessageSquare size={20} className="text-orange" />
                        <h2 className="h-display text-2xl font-semibold">Avis de la communauté</h2>
                        <span className="text-xs font-mono text-muted2">{reviews.length} avis</span>
                    </div>
                    {loading ? (
                        <div className="text-center text-muted2 py-10">Chargement…</div>
                    ) : reviews.length === 0 ? (
                        <div className="text-center text-muted2 py-10 text-sm">
                            Aucun avis pour l'instant. Soyez le premier à donner votre retour !
                        </div>
                    ) : (
                        <div className="grid sm:grid-cols-2 gap-3 mt-6">
                            {reviews.map((r) => (
                                <ReviewCard key={r.id} r={r} />
                            ))}
                        </div>
                    )}
                </section>
            </main>
        </div>
    );
}

const ReviewCard = ({ r }) => (
    <div className="panel p-5" data-testid={`review-card-${r.id}`}>
        <div className="flex gap-0.5 mb-3">
            {[1, 2, 3, 4, 5].map((n) => (
                <Star key={n} size={14} className={n <= r.rating ? "text-orange" : "text-rule"} fill={n <= r.rating ? "currentColor" : "none"} />
            ))}
        </div>
        <p className="text-sm leading-relaxed whitespace-pre-wrap">{r.comment}</p>
        <div className="mt-4 pt-3 border-t border-rule flex items-center justify-between text-xs">
            <div>
                <div className="font-semibold text-ink">{r.name}</div>
                <div className="font-mono text-muted2">
                    {r.role_label}{r.etablissement ? ` · ${r.etablissement}` : ""}
                </div>
            </div>
            <div className="font-mono text-muted2">
                {new Date(r.created_at).toLocaleDateString("fr-FR")}
            </div>
        </div>
    </div>
);
